/**
 * R-68 — Centralized severity heuristics per constraint type.
 */
export type ConstraintType =
  | 'flood_zone'
  | 'interconnection_queue'
  | 'wetland_coverage'
  | 'water_stress'
  | 'grid_capacity';

export type ConstraintSeverity = 'block' | 'warn' | 'info';

const BLOCK_FLOOD_ZONES = new Set(['V', 'VE', 'AE', 'AO', 'AH', 'A']);

/** Largest queue duration (in years) we treat as physically plausible. */
export const MAX_PLAUSIBLE_QUEUE_YEARS = 10;

export interface ParsedQueueDuration {
  /** Upper bound of the range, in years (months converted at /12). */
  years: number;
  /** Lower bound of the range, in the native unit (undefined if not a range). */
  rangeLow?: number;
  /** Upper bound of the range, in the native unit (equals the single value if not a range). */
  rangeHigh: number;
  /** Native unit token as parsed from the value. */
  unit: 'years' | 'months';
}

/**
 * Parse a queue duration into its native unit AND a years-normalized upper bound.
 * Critically distinguishes "24-48 months" (→ 2-4 years) from "24-48 years":
 * a bare integer with no unit token is assumed to be years (legacy behaviour),
 * but an explicit `month`/`mo` token is honoured and converted at /12.
 */
export function parseQueueDuration(value: string | number): ParsedQueueDuration {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { years: value, rangeHigh: value, unit: 'years' };
  }
  const s = String(value);
  const isMonths = /\b(?:months?|mos?\b|mo\.)/i.test(s);
  const unit: 'years' | 'months' = isMonths ? 'months' : 'years';
  const toYears = (n: number) => (isMonths ? n / 12 : n);

  // Range: "24-48 months", "24–48 years", "24 to 48 yr"
  const range = s.match(/(\d+(?:\.\d+)?)\s*[-–—]+\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s+to\s+(\d+(?:\.\d+)?)/i);
  if (range) {
    const lo = parseFloat(range[1] ?? range[3]);
    const hi = parseFloat(range[2] ?? range[4]);
    return { years: toYears(Math.max(lo, hi)), rangeLow: Math.min(lo, hi), rangeHigh: Math.max(lo, hi), unit };
  }
  const bare = s.match(/(\d+(?:\.\d+)?)/);
  const n = bare ? parseFloat(bare[1]) : 0;
  return { years: toYears(n), rangeHigh: n, unit };
}

/** Parse queue duration to years (uses upper bound of ranges; months converted at /12). */
export function parseQueueYears(value: string | number): number {
  return parseQueueDuration(value).years;
}

export function normalizeConstraintValue(type: ConstraintType, value: string | number): string {
  switch (type) {
    case 'flood_zone':
      return String(value).toUpperCase().replace(/^ZONE\s+/i, '').trim();
    case 'interconnection_queue':
      return String(parseQueueYears(value));
    case 'wetland_coverage':
      return String(typeof value === 'number' ? value : parseFloat(String(value)) || 0);
    case 'water_stress':
      return String(value).trim();
    case 'grid_capacity':
      return String(value).trim();
    default:
      return String(value);
  }
}

export function severityForConstraint(type: ConstraintType, value: string | number): ConstraintSeverity {
  switch (type) {
    case 'flood_zone': {
      const z = normalizeConstraintValue(type, value);
      if (BLOCK_FLOOD_ZONES.has(z)) return 'block';
      if (z === 'X' || z === 'C') return 'info';
      return 'warn';
    }
    case 'interconnection_queue': {
      const years = parseQueueYears(value);
      if (years > 3) return 'block';
      if (years >= 2) return 'warn';
      return 'info';
    }
    case 'wetland_coverage': {
      const pct = typeof value === 'number' ? value : parseFloat(String(value));
      if (!Number.isFinite(pct)) return 'warn';
      if (pct >= 30) return 'block';
      if (pct >= 15) return 'warn';
      return 'info';
    }
    case 'water_stress': {
      const level = String(value);
      if (level === 'High') return 'block';
      if (level === 'Moderate') return 'warn';
      return 'info';
    }
    case 'grid_capacity': {
      const cap = String(value);
      if (cap === 'Constrained') return 'block';
      if (cap === 'Limited') return 'warn';
      return 'info';
    }
    default:
      return 'warn';
  }
}

/** Human-readable label for constraint type. */
export function constraintTypeLabel(type: ConstraintType): string {
  const labels: Record<ConstraintType, string> = {
    flood_zone: 'FEMA flood zone',
    interconnection_queue: 'Interconnection queue',
    wetland_coverage: 'Wetland coverage',
    water_stress: 'Water stress',
    grid_capacity: 'Grid capacity',
  };
  return labels[type];
}

/** Canonical phrase used for Key Findings substring matching. */
export function canonicalPhrase(type: ConstraintType, value: string | number): string {
  const norm = normalizeConstraintValue(type, value);
  switch (type) {
    case 'flood_zone':
      return `FEMA flood zone ${norm}`;
    case 'interconnection_queue': {
      const years = parseQueueYears(value);
      return `interconnection queue ${years} year`;
    }
    case 'wetland_coverage':
      return `wetland ${norm}%`;
    case 'water_stress':
      return `water stress ${norm}`;
    case 'grid_capacity':
      return `grid capacity ${norm}`;
    default:
      return `${type} ${norm}`;
  }
}

/** Display value for UI and suggested bullets. */
export function formatConstraintValue(type: ConstraintType, value: string | number): string {
  switch (type) {
    case 'flood_zone':
      return `Zone ${normalizeConstraintValue(type, value)}`;
    case 'interconnection_queue': {
      const parsed = parseQueueDuration(value);
      if (parsed.rangeLow != null && parsed.rangeLow !== parsed.rangeHigh) {
        // Preserve the full range with its native unit, e.g. "24-48 months".
        return `${parsed.rangeLow}-${parsed.rangeHigh} ${parsed.unit}`;
      }
      const single = parsed.rangeHigh;
      if (parsed.unit === 'months') {
        return `${single} month${single === 1 ? '' : 's'}`;
      }
      // Years: sanity guard — a queue of >10 years is non-physical (almost
      // always a months/years unit mix-up); clamp the displayed value so the
      // overstatement does not leak into Key Findings / structuredFindings.
      const yrs = Math.min(single, MAX_PLAUSIBLE_QUEUE_YEARS);
      return `${yrs} year${yrs === 1 ? '' : 's'}`;
    }
    case 'wetland_coverage': {
      const pct = typeof value === 'number' ? value : parseFloat(String(value));
      if (!Number.isFinite(pct)) return `${normalizeConstraintValue(type, value)}% of site`;
      // Round to 1 decimal so displays read "54.4%" not "54.44841143510744%".
      const rounded = Math.round(pct * 10) / 10;
      return `${rounded}% of site`;
    }
    case 'water_stress':
    case 'grid_capacity':
      return String(value);
    default:
      return String(value);
  }
}
